import type { FormEditorEntity } from "../form-editor-entity.types";
import type { BuilderFieldData, PaletteFieldType } from "../types";
import {
	builderFieldDataFromTemplate,
	createPaletteTemplateField,
} from "./palette-form-field-factory";
import { PALETTE_FIELD_DEFAULT_LABELS } from "./palette-metadata";

function fieldIdSegmentForApiName(fieldId: string): string {
	return fieldId.replace(/-/g, "_");
}

/**
 * Returns a sanitized label segment for API `name`, or `null` when the label
 * yields no usable identifier (empty after trim, or only symbols).
 */
function sanitizeLabelToApiNameSegment(label: string): string | null {
	const trimmed = label.trim();
	if (trimmed === "") return null;
	const sanitized = trimmed
		.replace(/\s+/g, "_")
		.replace(/[^\p{L}\p{N}_]/gu, "_")
		.replace(/_+/g, "_")
		.replace(/^_|_$/g, "");
	return sanitized === "" ? null : sanitized;
}

/**
 * Builds a unique API `FieldDefinition.name` from the field's initial label and stable id.
 * The backend treats `name` as immutable after the first create; only `label` may change later.
 *
 * When the label produces a non-empty slug: `{slug}_{idSegment}`. Otherwise: `field_{idSegment}` only
 * (no duplicate id suffix).
 *
 * @param label - Resolved display label (already defaulted when empty).
 * @param fieldId - Stable field id (UUID); hyphenated segments become underscores in the name.
 */
export function uniqueFieldDefinitionApiName(
	label: string,
	fieldId: string,
): string {
	const idSegment = fieldIdSegmentForApiName(fieldId);
	const slug = sanitizeLabelToApiNameSegment(label);
	if (slug === null) return `field_${idSegment}`;
	return `${slug}_${idSegment}`;
}

/**
 * Creates default {@link BuilderFieldData} for a palette type using the same templates as
 * {@link projectBuilderFieldToFormField} (via {@link createPaletteTemplateField}).
 *
 * @param label - User-visible default label (from i18n at creation time).
 */
export function createDefaultField(
	fieldType: PaletteFieldType,
	id: string,
	label: string,
	formEditorEntity: FormEditorEntity = "ticket",
): BuilderFieldData {
	const effectiveLabel =
		label.trim() !== "" ? label : PALETTE_FIELD_DEFAULT_LABELS[fieldType];
	const name = uniqueFieldDefinitionApiName(effectiveLabel, id);
	const template = createPaletteTemplateField(
		fieldType,
		{
			id,
			name,
			label: effectiveLabel,
		},
		formEditorEntity,
	);
	return builderFieldDataFromTemplate(fieldType, template);
}
